// ---------------------------------------------------------------------------
// KanbanBoard — column board (props in → UI out).
//
// Groups one flat row set into labelled columns and renders a card per row.
// Styling is delegated entirely to the .kanban / .kanban__col / .kanban__col-hd
// / .kanban__card family in shared/styles/components.css (token-backed, ported
// from the prototype's KanbanView). This is a pure rendering of the rows the
// caller already holds — it does NOT fetch, sort, or filter; membership and
// order are the caller's. A row whose `columnOf` returns an id not present in
// `columns` is dropped (no orphan column is invented).
//
// Accessibility: each column is a <section aria-label> so a screen reader can
// navigate the board region-by-region; the column header text is the label.
// Each card is keyboard-focusable (tabIndex 0) and activates onCardClick on
// Enter / Space — matching the clickable-row contract used by the shared Table.
//
// Dumb and surface-agnostic: no data fetching, no privileged logic, no
// Norwegian strings — safe for the public, sessionless register bundle.
// ---------------------------------------------------------------------------

import { Heading, Paragraph } from '@digdir/designsystemet-react'

export interface KanbanColumn {
  /** Stable column id; matched against the value returned by `columnOf`. */
  id: string
  /** Visible column header (already i18n'd by the caller). */
  title: string
}

export interface KanbanBoardProps<T> {
  /** Column definitions. Order determines left-to-right column order. */
  columns: KanbanColumn[]
  /** Flat row set; each row is placed in the column whose id `columnOf` returns. */
  rows: T[]
  /** Extracts a stable unique string key from a row (React key). */
  rowKey: (row: T) => string
  /** Maps a row to the id of the column it belongs in. */
  columnOf: (row: T) => string
  /** Renders the card body for a row. */
  renderCard: (row: T) => React.ReactNode
  /** Called when a card is activated (click / Enter / Space). */
  onCardClick?: (row: T) => void
}

/**
 * Grouped column board.
 *
 * Usage:
 *   <KanbanBoard
 *     columns={[{ id: 'new', title: t('…') }, …]}
 *     rows={cases}
 *     rowKey={(c) => c.uuid}
 *     columnOf={(c) => stageOf(c.current_state)}
 *     renderCard={(c) => <CaseCard row={c} variant="kanban" t={t} />}
 *     onCardClick={(c) => router.visit(`/cases/${c.uuid}`)}
 *   />
 */
export function KanbanBoard<T>({
  columns,
  rows,
  rowKey,
  columnOf,
  renderCard,
  onCardClick,
}: KanbanBoardProps<T>) {
  // Bucket the rows once, preserving caller order within each column.
  const byColumn = new Map<string, T[]>(columns.map((c) => [c.id, []]))
  for (const row of rows) {
    const bucket = byColumn.get(columnOf(row))
    if (bucket) bucket.push(row)
  }

  return (
    <div className="kanban">
      {columns.map((column) => {
        const items = byColumn.get(column.id) ?? []
        return (
          <section key={column.id} className="kanban__col" aria-label={column.title}>
            <div className="kanban__col-hd">
              <Heading level={2} data-size="2xs">{column.title}</Heading>
              <Paragraph asChild data-size="xs"><span>{items.length}</span></Paragraph>
            </div>
            {items.map((row) => {
              const key = rowKey(row)
              return (
                <div
                  key={key}
                  className="kanban__card"
                  // A card is keyboard-operable: a tab stop that activates on
                  // Enter / Space (WCAG 2.1.1), focus-visible from components.css.
                  tabIndex={onCardClick ? 0 : undefined}
                  onClick={onCardClick ? () => onCardClick(row) : undefined}
                  onKeyDown={
                    onCardClick
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            onCardClick(row)
                          }
                        }
                      : undefined
                  }
                >
                  {renderCard(row)}
                </div>
              )
            })}
          </section>
        )
      })}
    </div>
  )
}
